import { useAiChat } from "@builder/assist/services/useAiChat";
import { useState, useEffect, useMemo } from "react";
import "./chat.scss";
import { AssistantMessage, UserMessage } from "./components/Messages";
import DynamicIcon from "@common/components/DynamicIcon";

const Chat = () => {
    const { messages, handleSubmit, status, steps, abort } = useAiChat();
    const [input, setInput] = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey && status !== "streaming") {
            e.preventDefault();
            handleSubmit(input, attachments);
            setInput("");
            setAttachments([]);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setAttachments(prev => [...prev, ...files]);
        e.target.value = "";
    };

    const triggerFileSelect = () => {
        document.getElementById("assist-file-input")?.click();
    };

    const previewUrls = useMemo(() => attachments.map(file => URL.createObjectURL(file)), [attachments]);

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        let imageFile: File | undefined;

        // Safari/Chrome
        for (const item of Array.from(e.clipboardData?.items || [])) {
            if (item.type.startsWith("image/")) {
                imageFile = item.getAsFile() || undefined;
                break;
            }
        }
        // Firefox fallback
        if (!imageFile) {
            const file = Array.from(e.clipboardData?.files || []).find(f => f.type.startsWith("image/"));
            if (file) imageFile = file;
        }

        if (imageFile) {
            console.log("Uploading image from clipboard");
            e.preventDefault();
            setAttachments(prev => [...prev, imageFile!]);
        }
    };

    useEffect(() => {
        return () => {
            previewUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [previewUrls]);

    return (
        <div className="chat-container">
            <div className="chat-messages">
                {messages.map((message, index) =>
                    message.role === "user" ? (
                        <UserMessage message={message} key={message.id} />
                    ) : (
                       <AssistantMessage message={message} steps={index === messages.length - 1 ? steps : undefined} key={message.id} />
                    )
                )}
            </div>
            <div className="chat-input-wrapper">
                <textarea
                    value={input}
                    placeholder="What would you like to change?"
                    className="assist-chat-input"
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                />
                <input
                    type="file"
                    id="assist-file-input"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />

                {previewUrls.length > 0 && (
                    <div className="assist-attachment-previews">
                        {previewUrls.map((src, idx) => (
                            <div key={idx} className="assist-preview-wrapper"> 
                                <button type="button" className="assist-remove-btn" onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}>×</button>
                                <img src={src} alt={`preview-${idx}`} className="assist-attachment-preview" />
                            </div>
                        ))}
                    </div>
                )}

                <div className="chat-controllers">
                    <button type="button" className="assist-upload-button" onClick={triggerFileSelect}>
                        <img src={"/attachment.svg"} alt="upload" />
                        <span>Upload</span>
                    </button>
                    {status === "streaming" && (
                        <button type="button" className="assist-abort-button" onClick={abort}>
                            <DynamicIcon name="Circle-stop-2" colors={["var(--base-blue)", "#fff"]} />
                        </button>
                    )}
                </div>
           
            </div>
        </div>
    );
};

export default Chat;
